import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getFirestore, Firestore, doc, setDoc, getDoc } from "firebase/firestore";
import { getAuth, Auth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, User } from "firebase/auth";
import { getStorage, FirebaseStorage } from "firebase/storage";
import { getFunctions, Functions } from "firebase/functions";
import { getMessaging, getToken, Messaging } from "firebase/messaging";
import { useState, useEffect } from "react";

// Configuração de ambiente
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

let app: FirebaseApp | undefined;
let db: Firestore | undefined;
let auth: Auth | undefined;
let storage: FirebaseStorage | undefined;
let functions: Functions | undefined;
let messaging: Messaging | undefined;

// Inicialização segura
console.log("Tentando inicializar Firebase. Config:", {
  apiKey: firebaseConfig.apiKey ? "Presente" : "Missing",
  appsLength: getApps().length
});

if (firebaseConfig.apiKey && getApps().length === 0) {
  try {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
    auth = getAuth(app);
    storage = getStorage(app);
    functions = getFunctions(app, 'us-central1'); // Region padrão
    try {
      messaging = getMessaging(app);
    } catch (e) { console.warn("Messaging FCM não suportado no navegador", e); }
    console.log("Firebase conectado com sucesso!");
  } catch (error) {
    console.error("Falha ao inicializar Firebase:", error);
  }
} else if (getApps().length > 0) {
  app = getApps()[0];
  db = getFirestore(app);
  auth = getAuth(app);
  storage = getStorage(app);
  functions = getFunctions(app, 'us-central1');
  try {
    messaging = getMessaging(app);
  } catch (e) { }
  console.log("Firebase recuperado de instância existente.");
} else {
  console.error("Firebase falhou: API Key ausente ou erro desconhecido.");
}

// Hook de Autenticação + Role
export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<'user' | 'admin' | 'subscriber' | null>(null);
  const [vipExpiresAt, setVipExpiresAt] = useState<Date | null>(null);
  const [dailyLupas, setDailyLupas] = useState<number>(0);
  const [extraLupas, setExtraLupas] = useState<number>(0);
  const [adLupas, setAdLupas] = useState<number>(0);
  const [adUsesToday, setAdUsesToday] = useState<number>(0);
  const [unlockedCases, setUnlockedCases] = useState<string[]>([]);
  const [archiveKeys, setArchiveKeys] = useState<number>(0);
  const [dailyAdUnlocks, setDailyAdUnlocks] = useState<number>(0);
  const [dailyAcmeCreations, setDailyAcmeCreations] = useState<number>(0);
  const [reputacao, setReputacao] = useState<number>(0);
  const [patentesRecompensadas, setPatentesRecompensadas] = useState<number[]>([]);
  const [lastCaseInteractionDate, setLastCaseInteractionDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u && db) {
        // Sync user to Firestore and get Role
        const userRef = doc(db, 'users', u.uid);
        try {
          const snap = await getDoc(userRef);
          if (snap.exists()) {
            const data = snap.data();
            let currentRole = data.role || 'user';
            let expiresAt: Date | null = null;
            let currentDailyLupas = data.dailyLupas !== undefined ? data.dailyLupas : 15;
            let currentExtraLupas = data.extraLupas || 0;
            let currentAdLupas = data.adLupas || 0;
            let currentAdUses = data.adUsesToday || 0;
            let currentArchiveKeys = data.archiveKeys || 0;
            let currentDailyAdUnlocks = data.dailyAdUnlocks || 0;
            let currentDailyAcmeCreations = data.dailyAcmeCreations || 0;
            let lastLupaResetDate = data.lastLupaResetDate ? new Date(data.lastLupaResetDate.seconds * 1000) : new Date();
            let lastAdDate = data.lastAdDate ? new Date(data.lastAdDate.seconds * 1000) : new Date();
            let lastAdUnlockDate = data.lastAdUnlockDate ? new Date(data.lastAdUnlockDate.seconds * 1000) : new Date();
            let lastAcmeDate = data.lastAcmeDate ? new Date(data.lastAcmeDate.seconds * 1000) : new Date();

            // Check VIP expiration
            if (currentRole === 'subscriber' && data.subscriptionExpiresAt) {
              const expiresAtPosix = data.subscriptionExpiresAt.seconds * 1000;
              if (Date.now() > expiresAtPosix) {
                currentRole = 'user'; // Expired
                // Client should not attempt to downgrade its own role in Firestore (Permission Denied)
                // This will be handled by the server or treated as 'user' locally.
              } else {
                expiresAt = new Date(expiresAtPosix);
              }
            }

            setRole(currentRole);
            setVipExpiresAt(expiresAt);

            // Check Daily Reset for Lupas (Non-VIPs get 15 everyday)
            const today = new Date();
            let needsReset = false;
            if (today.getDate() !== lastLupaResetDate.getDate() || today.getMonth() !== lastLupaResetDate.getMonth() || today.getFullYear() !== lastLupaResetDate.getFullYear()) {
              if (currentRole !== 'subscriber' && currentRole !== 'admin') {
                // Hard reset to 15 (no accumulation for daily)
                currentDailyLupas = 15;
              }
              needsReset = true;
              lastLupaResetDate = today;
            }

            // Check Ad Limit Reset
            if (today.getDate() !== lastAdDate.getDate() || today.getMonth() !== lastAdDate.getMonth() || today.getFullYear() !== lastAdDate.getFullYear()) {
              currentAdUses = 0;
              lastAdDate = today;
              needsReset = true; // reusing needsReset as a generic DB update flag hook
            }

            // Check Case Ad Unlock Reset
            if (today.getDate() !== lastAdUnlockDate.getDate() || today.getMonth() !== lastAdUnlockDate.getMonth() || today.getFullYear() !== lastAdUnlockDate.getFullYear()) {
              currentDailyAdUnlocks = 0;
              lastAdUnlockDate = today;
              needsReset = true;
            }

            // Check Clube ACME Gen Reset
            if (today.getDate() !== lastAcmeDate.getDate() || today.getMonth() !== lastAcmeDate.getMonth() || today.getFullYear() !== lastAcmeDate.getFullYear()) {
              currentDailyAcmeCreations = 0;
              lastAcmeDate = today;
              needsReset = true;
            }

            let currentReputacao = data.reputacao || 0;
            let currentPatentesRecompensadas = data.patentes_recompensadas || [];
            let currentLastInteractionDate = data.lastCaseInteractionDate ? new Date(data.lastCaseInteractionDate.seconds * 1000) : null;

            if (currentLastInteractionDate && currentReputacao > 0 && currentRole !== 'admin') {
              const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
              const lastInteractionMidnight = new Date(currentLastInteractionDate.getFullYear(), currentLastInteractionDate.getMonth(), currentLastInteractionDate.getDate());

              const diffTime = todayMidnight.getTime() - lastInteractionMidnight.getTime();
              const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

              if (diffDays >= 1) {
                const penalty = diffDays * 5;
                const expectedLoss = Math.min(currentReputacao, penalty);
                currentReputacao = Math.max(0, currentReputacao - penalty);
                currentLastInteractionDate = todayMidnight;
                needsReset = true;

                if (expectedLoss > 0) {
                  localStorage.setItem('pendingPenaltyMessage', `Aviso da Corregedoria: Você perdeu ${expectedLoss} pontos de Reputação por ${diffDays} dia(s) de ausência na Agência.`);
                }
              }
            }

            setDailyLupas(currentDailyLupas);
            setExtraLupas(currentExtraLupas);
            setAdLupas(currentAdLupas);
            setAdUsesToday(currentAdUses);
            setUnlockedCases(data.unlockedCases || []);
            setArchiveKeys(currentArchiveKeys);
            setDailyAdUnlocks(currentDailyAdUnlocks);
            setDailyAcmeCreations(currentDailyAcmeCreations);
            setReputacao(currentReputacao);
            setPatentesRecompensadas(currentPatentesRecompensadas);
            setLastCaseInteractionDate(currentLastInteractionDate);

            // Update last login
            const updateData: any = { lastLogin: new Date(), email: u.email, name: u.displayName, role: currentRole };
            if (needsReset) {
              updateData.dailyLupas = currentDailyLupas;
              updateData.lastLupaResetDate = lastLupaResetDate;
              updateData.lastAdDate = lastAdDate;
              updateData.adUsesToday = currentAdUses;
              updateData.lastAdUnlockDate = lastAdUnlockDate;
              updateData.dailyAdUnlocks = currentDailyAdUnlocks;
              updateData.lastAcmeDate = lastAcmeDate;
              updateData.dailyAcmeCreations = currentDailyAcmeCreations;

              if (currentLastInteractionDate) {
                updateData.reputacao = currentReputacao;
                updateData.lastCaseInteractionDate = currentLastInteractionDate;
              }
            }
            if (data.dailyLupas === undefined || data.extraLupas === undefined || data.adLupas === undefined || data.archiveKeys === undefined) {
              updateData.dailyLupas = currentDailyLupas;
              updateData.extraLupas = currentExtraLupas;
              updateData.adLupas = currentAdLupas;
              updateData.archiveKeys = currentArchiveKeys;
              updateData.dailyAdUnlocks = currentDailyAdUnlocks;
              updateData.dailyAcmeCreations = currentDailyAcmeCreations;
              updateData.lastLupaResetDate = lastLupaResetDate;
              updateData.lastAdDate = lastAdDate;
              updateData.adUsesToday = currentAdUses;
              updateData.lastAdUnlockDate = lastAdUnlockDate;
              updateData.lastAcmeDate = lastAcmeDate;
            }
            
            // SECURITY: Never try to update 'role' from the client unless we know for sure what we're doing
            // delete updateData.role; // This ensures we don't hit "Missing or insufficient permissions"
            const { role: _, ...safeUpdateData } = updateData;

            setDoc(userRef, safeUpdateData, { merge: true }).catch(console.error);
          } else {
            // Create new user doc
            const newRole = 'user'; // Default role
            await setDoc(userRef, {
              email: u.email,
              name: u.displayName,
              role: newRole,
              createdAt: new Date(),
              lastLogin: new Date(),
              gameState: null,
              dailyLupas: 15,
              extraLupas: 0,
              adLupas: 0,
              adUsesToday: 0,
              archiveKeys: 0,
              dailyAdUnlocks: 0,
              dailyAcmeCreations: 0,
              reputacao: 0,
              patentes_recompensadas: [],
              lastCaseInteractionDate: null,
              lastAdDate: new Date(),
              lastLupaResetDate: new Date(),
              lastAdUnlockDate: new Date(),
              lastAcmeDate: new Date()
            });
            setRole(newRole);
            setDailyLupas(15);
            setExtraLupas(0);
            setAdLupas(0);
            setAdUsesToday(0);
            setUnlockedCases([]);
            setArchiveKeys(0);
            setDailyAdUnlocks(0);
            setDailyAcmeCreations(0);
            setReputacao(0);
            setPatentesRecompensadas([]);
            setLastCaseInteractionDate(null);
          }
        } catch (err) {
          console.error("Erro ao buscar perfil do usuário:", err);
          setRole('user'); // Fallback
        }
      } else {
        setRole(null);
        setVipExpiresAt(null);
        setDailyLupas(0);
        setExtraLupas(0);
        setAdLupas(0);
        setAdUsesToday(0);
        setUnlockedCases([]);
        setArchiveKeys(0);
        setDailyAdUnlocks(0);
        setDailyAcmeCreations(0);
        setReputacao(0);
        setPatentesRecompensadas([]);
        setLastCaseInteractionDate(null);
      }
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  return {
    user, role, vipExpiresAt, loading,
    dailyLupas, setDailyLupas,
    extraLupas,
    adLupas, setAdLupas,
    adUsesToday, setAdUsesToday,
    unlockedCases, setUnlockedCases,
    archiveKeys, setArchiveKeys,
    dailyAdUnlocks, setDailyAdUnlocks,
    dailyAcmeCreations, setDailyAcmeCreations,
    reputacao, setReputacao,
    patentesRecompensadas, setPatentesRecompensadas,
    lastCaseInteractionDate, setLastCaseInteractionDate
  };
};

// Funções de Auth
export const signInWithGoogle = async () => {
  if (!auth) throw new Error("Firebase Auth não inicializado.");
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error("Erro no login:", error);
    throw error;
  }
};

export const logout = async () => {
  if (!auth) return;
  await signOut(auth);
};

export const registerPushNotifications = async (userId: string) => {
  if (!messaging || !db) return null;
  try {
    const swUrl = `/firebase-messaging-sw.js?apiKey=${firebaseConfig.apiKey}&projectId=${firebaseConfig.projectId}&messagingSenderId=${firebaseConfig.messagingSenderId}&appId=${firebaseConfig.appId}`;
    const registration = await navigator.serviceWorker.register(swUrl);

    // Obter Token do Firebase
    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    const token = await getToken(messaging, {
      serviceWorkerRegistration: registration,
      vapidKey: vapidKey
    });

    if (token) {
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, { fcmToken: token }, { merge: true });
      return token;
    }
  } catch (error) {
    console.error('Erro ao registrar push notifications:', error);
  }
  return null;
};

export { db, auth, storage, functions, messaging, firebaseConfig };